import { useState } from "react";
import { X, Send, User, Mail, MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { InternationalPhoneInput } from "@/components/ui/international-phone-input";
import "@/components/ui/international-phone-input.css";
import "./BookingModal.css";
import axios from "axios";
import baseURL from "@/http";

interface BookingModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function BookingModal({ isOpen, onClose }: BookingModalProps) {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    enquiry_for: "",
    question: "",
    source: { "source_category": "WEBSITE_FORM" }
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const purposes = [
    "Schedule a Product Demo",
    "Get Pricing Information",
    "Educational Institution Inquiry",
    "Corporate Training Solution",
    "Technical Support",
    "Partnership Opportunity",
    "Other",
  ];

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(
        `${baseURL}/public/WebsiteForm`,
        formData, // send form data
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      console.log("Form submitted:", response.data);

      setIsSubmitting(false);
      setIsSubmitted(true);

      // Reset after success
      setTimeout(() => {
        setIsSubmitted(false);
        setFormData({
          name: "",
          email: "",
          phone: "",
          enquiry_for: "",
          question: "",
          source: { "source_category": "WEBSITE_FORM" }
        });
        onClose();
      }, 3000);
    } catch (error: any) {
      setIsSubmitting(false);
      console.error("Error:", error);
      alert("Something went wrong! Please try again.");
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
      setIsSubmitted(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="booking-modal-overlay" onClick={handleClose}>
      <div className="booking-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="booking-modal__header">
          <div className="booking-modal__header-content">
            <h2 className="booking-modal__title">Book a Free Demo</h2>
            {/* <p className="booking-modal__subtitle">
              Let's discuss how we can help transform your educational experience
            </p> */}
          </div>
          <button
            className="booking-modal__close"
            onClick={handleClose}
            disabled={isSubmitting}
            aria-label="Close modal"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="booking-modal__content">
          {isSubmitted ? (
            <div className="booking-modal__success">
              <div className="booking-modal__success-icon">
                <Send className="w-8 h-8" />
              </div>
              <h3 className="booking-modal__success-title">Thank You!</h3>
              <p className="booking-modal__success-text">
                We've received your request. Our team will contact you within 24
                hours to schedule your personalized demo.
              </p>
            </div>
          ) : (
            <form className="booking-modal__form" onSubmit={handleSubmit}>
              {/* Name Field */}
              <div className="booking-modal__field">
                <label className="booking-modal__label">
                  <User className="booking-modal__label-icon" />
                  Full Name *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Enter your full name"
                  required
                  disabled={isSubmitting}
                  className="booking-modal__input"
                />
              </div>

              {/* Email Field */}
              <div className="booking-modal__field">
                <label className="booking-modal__label">
                  <Mail className="booking-modal__label-icon" />
                  Email Address *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="your.email@example.com"
                  required
                  disabled={isSubmitting}
                  className="booking-modal__input"
                />
              </div>

              {/* Phone Field with International Input */}
              <div className="booking-modal__field">
                <label className="booking-modal__label">
                  Phone Number *
                </label>
                <InternationalPhoneInput
                  value={formData.phone}
                  onChange={(value) =>
                    setFormData((prev) => ({ ...prev, phone: value }))
                  }
                  placeholder="Enter your phone number"
                  disabled={isSubmitting}
                />
              </div>

              {/* Purpose Field */}
              <div className="booking-modal__field">
                <label className="booking-modal__label">
                  <MessageSquare className="booking-modal__label-icon" />
                  Purpose *
                </label>
                <select
                  name="enquiry_for"
                  value={formData.enquiry_for}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                  className="booking-modal__select"
                >
                  <option value="">Select the purpose of your inquiry</option>
                  {purposes.map((purpose) => (
                    <option key={purpose} value={purpose}>
                      {purpose}
                    </option>
                  ))}
                </select>
              </div>

              {/* Message Field */}
              <div className="booking-modal__field">
                <label className="booking-modal__label">Message (Optional)</label>
                <textarea
                  name="question"
                  value={formData.question}
                  onChange={handleInputChange}
                  placeholder="Tell us more about your requirements..."
                  disabled={isSubmitting}
                  className="booking-modal__textarea"
                  rows={3}
                />
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                className="booking-modal__submit"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <div className="booking-modal__spinner" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    Submit Request
                  </>
                )}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
